import dayjs from 'dayjs'
import {
    MailBatchSummary,
    MailBatchSyntheticStatus,
    MailerFilterField,
    MailerFormValues,
    MailerRecipients
} from '@/features/mailer/types'
import {ALLOWED_ATTACHMENT_EXTENSIONS, ALLOWED_ATTACHMENT_TYPES} from '@/features/mailer/constants'

export const uniq = (ids: string[]): string[] => [...new Set(ids)]

// RHF number inputs can yield strings or NaN: normalize before any arithmetic
// ("60" + 1 would concatenate to "601" → birthDate >= year 1425, a no-op filter).
const toAge = (value: unknown): number | undefined => {
    if (value === '' || value === null || value === undefined) return undefined
    const age = Number(value)
    return Number.isFinite(age) && age >= 0 ? age : undefined
}

/**
 * Serializes the compose form into the recipients payload shared by
 * POST /mailer/send and POST /mailer/recipients/preview (OPSSTD-3643,
 * consent/sector keys added by OPSSTD-3648).
 */
export const buildRecipientsPayload = (values: MailerFormValues): MailerRecipients => {
    const recipients: MailerRecipients = {}

    if (values.includePatients) {
        const fields: MailerFilterField[] = []

        if (values.patientStatus !== 'all') {
            fields.push({key: 'patientStatus', operator: 'is', value: values.patientStatus})
        }
        if (values.patientConsent !== 'all') {
            fields.push({key: 'consent', operator: 'is', value: values.patientConsent})
        }
        // Patients in both regimes (outpatient_domiciliary) match either selection
        // (OPSSTD-3648 mapping).
        if (values.patientSector === 'ambulatorial') {
            fields.push({key: 'sector', operator: 'in', value: ['outpatient', 'outpatient_domiciliary']})
        } else if (values.patientSector === 'home') {
            fields.push({key: 'sector', operator: 'in', value: ['domiciliary', 'outpatient_domiciliary']})
        }
        // Both options selected (or none) means "no filter" per spec §13.
        if (values.patientServiceTypes.length === 1) {
            fields.push({key: 'serviceType', operator: 'is', value: values.patientServiceTypes[0]})
        }
        if (values.diagnosisEnabled && values.neuromuscularDiagnosisIds.length > 0) {
            fields.push({key: 'neuromuscularDiagnosisId', operator: 'in', value: values.neuromuscularDiagnosisIds})
        }
        if (values.priEnabled) {
            // The design splits each PRI sub-group into "Patologia 1" (PRINCIPALE)
            // and optional "Patologia 2" (SECONDARIA); the BE exposes a single
            // any-of dimension per sub-group, so we merge the two selections.
            const main = uniq([
                ...values.rehabPathology1Ids,
                ...(values.rehabPathology2Enabled ? values.rehabPathology2Ids : [])
            ])
            if (main.length > 0) {
                fields.push({key: 'pathologyMain', operator: 'in', value: main})
            }
            const other = uniq([
                ...values.otherPathology1Ids,
                ...(values.otherPathology2Enabled ? values.otherPathology2Ids : [])
            ])
            if (other.length > 0) {
                fields.push({key: 'pathologyOther', operator: 'in', value: other})
            }
        }
        // Age → birthDate conversion per contract §0.2 (OPSSTD-3643):
        // age ≥ N → birthDate <= today - N years
        // age ≤ M → birthDate >= today - (M+1) years + 1 day (inclusive boundary)
        // Min age 0 is a no-op (every age is >= 0); max age 0 is a real filter (< 1 year old).
        const minAge = toAge(values.patientMinAge)
        if (minAge !== undefined && minAge > 0) {
            const maxBirthDate = dayjs().subtract(minAge, 'year').format('YYYY-MM-DD')
            fields.push({key: 'birthDate', operator: '<=', value: maxBirthDate})
        }
        const maxAge = toAge(values.patientMaxAge)
        if (maxAge !== undefined) {
            const minBirthDate = dayjs()
                .subtract(maxAge + 1, 'year')
                .add(1, 'day')
                .format('YYYY-MM-DD')
            fields.push({key: 'birthDate', operator: '>=', value: minBirthDate})
        }
        if (values.patientGender !== 'all') {
            fields.push({key: 'gender', operator: 'is', value: values.patientGender})
        }
        if (values.patientAslDistrictIds.length > 0) {
            fields.push({key: 'residenceAslDistrictId', operator: 'in', value: values.patientAslDistrictIds})
        }
        // "Solo pazienti fuori regione" is BE-gated (OPSSTD-3643 §7: no region
        // filter key, not approximable) — intentionally NOT serialized.
        // Only include patients who have an email.
        fields.push({key: 'email', operator: 'is not', value: null})

        recipients.patients = {filters: {operator: 'and', fields}}
    }

    if (values.includeConsultants) {
        const fields: MailerFilterField[] = []

        if (values.consultantStatus !== 'all') {
            fields.push({key: 'status', operator: 'is', value: values.consultantStatus})
        }
        if (values.consultantGroup !== 'all') {
            fields.push({key: 'group', operator: 'is', value: values.consultantGroup})
        }
        if (values.consultantContractType !== 'all') {
            fields.push({key: 'contractType', operator: 'is', value: values.consultantContractType})
        }
        if (values.consultantSpecializationIds.length > 0) {
            fields.push({key: 'specializationId', operator: 'in', value: values.consultantSpecializationIds})
        }
        if (values.consultantWorkSector !== 'all') {
            fields.push({key: 'workSector', operator: 'is', value: values.consultantWorkSector})
        }
        // Only include consultants who have an email.
        fields.push({key: 'email', operator: 'is not', value: null})

        recipients.consultants = {filters: {operator: 'and', fields}}
    }

    return recipients
}

/**
 * Active-filter counters feeding the accordion badges and the "no filters"
 * warning. Mirrors the reference component semantics: any visible selection
 * counts, regardless of payload serialization.
 */
export const countPatientFilters = (values: MailerFormValues): number => {
    let count = 0
    if (values.patientStatus !== 'all') count++
    if (values.patientConsent !== 'all') count++
    if (values.patientSector !== 'all') count++
    if (values.patientServiceTypes.length > 0) count++
    if (values.diagnosisEnabled && values.neuromuscularDiagnosisIds.length > 0) count++
    if (values.priEnabled) {
        if (values.rehabPathology1Ids.length > 0) count++
        if (values.rehabPathology2Enabled && values.rehabPathology2Ids.length > 0) count++
        if (values.otherPathology1Ids.length > 0) count++
        if (values.otherPathology2Enabled && values.otherPathology2Ids.length > 0) count++
    }
    const minAge = toAge(values.patientMinAge)
    const maxAge = toAge(values.patientMaxAge)
    if ((minAge !== undefined && minAge > 0) || maxAge !== undefined) count++
    if (values.patientGender !== 'all') count++
    if (values.patientAslDistrictIds.length > 0) count++
    return count
}

export const countConsultantFilters = (values: MailerFormValues): number => {
    let count = 0
    if (values.consultantStatus !== 'all') count++
    if (values.consultantGroup !== 'all') count++
    if (values.consultantContractType !== 'all') count++
    if (values.consultantSpecializationIds.length > 0) count++
    if (values.consultantWorkSector !== 'all') count++
    return count
}

/**
 * Synthetic campaign status (contract §5): the FE derives it from counts.
 * pending > 0 → "In corso"; any failure/bounce → "Parziale"; else "Inviata".
 */
export const deriveBatchStatus = (batch: MailBatchSummary): MailBatchSyntheticStatus => {
    if (batch.counts.pending > 0) return 'sending'
    if (batch.counts.failed + batch.counts.bounced > 0) return 'partial'
    return 'sent'
}

const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

// The BE expects an HTML body (sanitized server-side); the UI edits plain text.
export const plainTextBodyToHtml = (text: string): string =>
    text
        .split(/\n{2,}/)
        .map(block => `<p>${escapeHtml(block).replace(/\n/g, '<br>')}</p>`)
        .join('')

export const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

// Accept the file if either its MIME type or its extension is whitelisted.
// Extension fallback covers PDFs/Office files where browsers occasionally
// return `file.type === ''` (e.g. drag-drop from external apps).
export const isAllowedAttachment = (file: File): boolean => {
    if (ALLOWED_ATTACHMENT_TYPES.includes(file.type)) return true
    const ext = file.name.split('.').pop()?.toLowerCase()
    return !!ext && ALLOWED_ATTACHMENT_EXTENSIONS.includes(ext)
}
